import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Space,
    Row,
    Col,
    Statistic,
    Progress,
    Descriptions,
    Button,
    Spin,
    Empty,
    Tag,
    Divider,
    Table,
    Badge,
    Tooltip,
    Modal,
    Form,
    Switch,
    InputNumber,
    Input,
    Select,
    TimePicker,
    DatePicker,
    message,
    ConfigProvider,
} from 'antd';
import {
    WifiOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    ClockCircleOutlined,
    UserOutlined,
    TeamOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DesktopOutlined,
    GlobalOutlined,
    ReloadOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getInternetFacility, createOrUpdateInternetFacility } from '../../core/center-accreditation/centerAccreditationApi';
import { CenterAccreditationInternetFacility as ScInternetFacilityType } from '../../core/center-accreditation/types';
import { convertToBoolean, generateTimeOptions, getDayLabel } from '../../core/utils/helpers';

import './ScInternet.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Title, Text } = Typography;
const { Option } = Select;

const timeOptions = generateTimeOptions();

const ScInternet: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { internetFacility, isInternetFacilityFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);
    const { userData } = useSelector((state: CombinedStates) => state.user);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());

    useEffect(() => {
        dispatch(getInternetFacility({}));
    }, []);

    // Set form values when internet facility data is loaded
    useEffect(() => {
        if (internetFacility) {
            form.setFieldsValue({
                ...internetFacility,
                // Convert string booleans to actual booleans
                has_website: convertToBoolean(internetFacility.has_website),
                website_visits_tracked: convertToBoolean(internetFacility.website_visits_tracked),
                has_internet_reading_room: convertToBoolean(internetFacility.has_internet_reading_room),
                services_paid: convertToBoolean(internetFacility.services_paid),
                has_remote_training: convertToBoolean(internetFacility.has_remote_training),
                // Convert website_features string booleans
                website_features: {
                    feedback_about_institution: convertToBoolean(internetFacility.website_features?.feedback_about_institution),
                    feedback_about_website: convertToBoolean(internetFacility.website_features?.feedback_about_website),
                    event_registration: convertToBoolean(internetFacility.website_features?.event_registration),
                    e_conferences: convertToBoolean(internetFacility.website_features?.e_conferences),
                    other: internetFacility.website_features?.other || '',
                },
                // Convert help_methods string booleans
                help_methods: {
                    bookable_consultation: convertToBoolean(internetFacility.help_methods?.bookable_consultation),
                    constant_consultation: convertToBoolean(internetFacility.help_methods?.constant_consultation),
                    ad_hoc_by_staff: convertToBoolean(internetFacility.help_methods?.ad_hoc_by_staff),
                    peer_help: convertToBoolean(internetFacility.help_methods?.peer_help),
                },
                // Convert motivation_methods string booleans
                motivation_methods: {
                    post_required_material_online: convertToBoolean(internetFacility.motivation_methods?.post_required_material_online),
                    announce_in_schools: convertToBoolean(internetFacility.motivation_methods?.announce_in_schools),
                    other: internetFacility.motivation_methods?.other || '',
                },
                reading_room_hours: {
                    mon: { from: internetFacility.reading_room_hours.mon?.from || null, to: internetFacility.reading_room_hours.mon?.to || null },
                    tue: { from: internetFacility.reading_room_hours.tue?.from || null, to: internetFacility.reading_room_hours.tue?.to || null },
                    wed: { from: internetFacility.reading_room_hours.wed?.from || null, to: internetFacility.reading_room_hours.wed?.to || null },
                    thu: { from: internetFacility.reading_room_hours.thu?.from || null, to: internetFacility.reading_room_hours.thu?.to || null },
                    fri: { from: internetFacility.reading_room_hours.fri?.from || null, to: internetFacility.reading_room_hours.fri?.to || null },
                    sat: { from: internetFacility.reading_room_hours.sat?.from || null, to: internetFacility.reading_room_hours.sat?.to || null },
                    sun: { from: internetFacility.reading_room_hours.sun?.from || null, to: internetFacility.reading_room_hours.sun?.to || null },
                },

            });
        }
    }, [internetFacility, form]);

    const handleSave = async (values: any) => {
        // No need to convert time ranges since they are already strings
        const requestData = {
            ...values,
            school_id: singleSchool?.id,
            id: internetFacility?.id,
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
        };

        await dispatch(createOrUpdateInternetFacility(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getInternetFacility({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    return (
        <ConfigProvider locale={locale}>
            <div className="school-internet">
                <Card title="Interneto įrangos duomenys" className="internet-form-card" loading={isInternetFacilityFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {internetFacility?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {internetFacility.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Tooltip title="Atnaujinti interneto įrangos duomenis">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefreshClick}
                                    loading={!!isInternetFacilityFetching}>
                                    Atnaujinti
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            computers_for_training_count: 0,
                            paid_db_count: 0,
                            free_db_count: 0,
                            has_website: false,
                            website_features: {
                                feedback_about_institution: false,
                                feedback_about_website: false,
                                event_registration: false,
                                e_conferences: false,
                                other: '',
                            },
                            website_visits_tracked: false,
                            has_internet_reading_room: false,
                            staff_full_time: 0,
                            staff_partial: 0,
                            visitor_workstations_count: 0,
                            services_paid: false,
                            reading_room_hours: {
                                mon: { from: null, to: null },
                                tue: { from: null, to: null },
                                wed: { from: null, to: null },
                                thu: { from: null, to: null },
                                fri: { from: null, to: null },
                                sat: { from: null, to: null },
                                sun: { from: null, to: null },
                            },
                            help_methods: {
                                bookable_consultation: false,
                                constant_consultation: false,
                                ad_hoc_by_staff: false,
                                peer_help: false,
                            },
                            motivation_methods: {
                                post_required_material_online: false,
                                announce_in_schools: false,
                                other: '',
                            },
                            load_percent: 0,
                            visitors_stats: {
                                total: 0,
                                institution_staff: 0,
                                lecturers: 0,
                                event_participants: 0,
                                region_teachers: 0,
                                students: 0,
                                other: {
                                    label: '',
                                    count: 0,
                                },
                            },
                            it_events_stats: {
                                seminar: 0,
                                course: 0,
                                lecture: 0,
                                total: 0,
                            },
                            has_remote_training: false,
                            remote_events_stats: {
                                seminar: 0,
                                course: 0,
                                lecture: 0,
                                total: 0,
                            },
                        }}>

                        {/* 2.1 Kiek kompiuterių su internetu naudojate klientų mokymui */}
                        <Card title="2.1 Kiek kompiuterių su internetu naudojate klientų mokymui? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="computers_for_training_count" label="Kompiuterių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.2 Keliomis duomenų bazėmis naudojatės */}
                        <Card title="2.2 Keliomis duomenų bazėmis naudojatės? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="paid_db_count" label="2.2.1 Mokamomis">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="free_db_count" label="2.2.2 Nemokamomis">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.3 Ar institucija turi interneto svetainę */}
                        <Card title="2.3 Ar institucija turi interneto svetainę?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="has_website" label="Turi interneto svetainę" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.4 Institucijos interneto svetainėje klientai gali */}
                        <Card title="2.4 Institucijos interneto svetainėje klientai gali:" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['website_features', 'feedback_about_institution']} label="2.4.1 Rašyti atsiliepimus apie institucijos veiklą" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['website_features', 'feedback_about_website']} label="2.4.2 Rašyti atsiliepimus apie interneto svetainę" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['website_features', 'event_registration']} label="2.4.3 Registruotis į renginius" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['website_features', 'e_conferences']} label="2.4.4 Dalyvauti elektroninėse konferencijose" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item name={['website_features', 'other']} label="2.4.5 Kita (įrašykite)">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.5 Ar fiksuojamas apsilankiusiųjų interneto svetainėje skaičius */}
                        <Card title="2.5 Ar fiksuojamas apsilankiusiųjų interneto svetainėje skaičius?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="website_visits_tracked" label="Fiksuojamas apsilankiusiųjų skaičius" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.6 Ar institucijoje įrengta interneto skaitykla */}
                        <Card title="2.6 Ar institucijoje įrengta interneto skaitykla?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="has_internet_reading_room" label="Įrengta interneto skaitykla" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.7 Interneto skaityklos personalas */}
                        <Card title="2.7 Interneto skaityklos personalas (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="staff_full_time" label="2.7.1 Nuolatiniai (etatiniai) darbuotojai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="staff_partial" label="2.7.2 Institucijos darbuotojai, turintys ir kitų pareigų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.8 Kiek darbo vietų skirta lankytojams naudotis internetu */}
                        <Card title="2.8 Kiek darbo vietų skirta lankytojams naudotis internetu? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="visitor_workstations_count" label="Darbo vietų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.9 Ar interneto skaityklos paslaugos lankytojams mokamos */}
                        <Card title="2.9 Ar interneto skaityklos paslaugos lankytojams mokamos?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="services_paid" label="Paslaugos mokamos" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.10 Interneto skaityklos darbo laikas */}
                        <Card title="2.10 Interneto skaityklos darbo laikas (įrašykite)" style={{ marginBottom: 16 }}>
                            <Row gutter={[24, 24]}>
                                <Col span={24}>
                                    <Title level={5}>Darbo laikas</Title>
                                    {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                                        <div key={day} style={{ marginBottom: 16 }}>
                                            <Text strong>{getDayLabel(day)}</Text>
                                            <Row gutter={8}>
                                                <Col span={12} style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                                                    <Form.Item
                                                        name={['reading_room_hours', day, 'from']}
                                                        label="Nuo"
                                                        style={{ marginBottom: 8 }}>
                                                        <Select
                                                            className="time-selection"
                                                            placeholder="Pradžia"
                                                            allowClear>
                                                            {timeOptions.map(option => (
                                                                <Option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                    <Form.Item
                                                        name={['reading_room_hours', day, 'to']}
                                                        label="Iki"
                                                        style={{ marginBottom: 8 }}>
                                                        <Select
                                                            className="time-selection"
                                                            placeholder="Pabaiga"
                                                            allowClear>
                                                            {timeOptions.map(option => (
                                                                <Option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))}
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.11 Ar padedama lankytojams naudotis internetu */}
                        <Card title="2.11 Ar padedama lankytojams naudotis internetu?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['help_methods', 'bookable_consultation']} label="2.11.1 Konsultavimo paslaugą galima užsakyti iš anksto" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['help_methods', 'constant_consultation']} label="2.11.2 Konsultuojama nuolat" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['help_methods', 'ad_hoc_by_staff']} label="2.11.3 Esant galimybei konsultuoja institucijos darbuotojai" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['help_methods', 'peer_help']} label="2.11.4 Lankytojai padeda vienas kitam" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.12 Kokiomis priemonėmis stengiatės motyvuoti klientus naudotis internetu */}
                        <Card title="2.12 Kokiomis priemonėmis stengiatės motyvuoti klientus naudotis internetu?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['motivation_methods', 'post_required_material_online']} label="2.12.1 Interneto svetainėje skelbiate mokytojams būtinai reikalingą medžiagą, prieinamą tik elektronine forma" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['motivation_methods', 'announce_in_schools']} label="2.12.2 Skelbiate ugdymo įstaigose apie galimybę naudotis interneto skaitykla" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item name={['motivation_methods', 'other']} label="2.12.3 Kitomis priemonėmis informuojate apie interneto skaityklą (įrašykite)">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.13 Koks interneto skaityklos apkrovimas */}
                        <Card title="2.13 Koks interneto skaityklos apkrovimas? (nurodykite procentais)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="load_percent" label="Apkrovimas (%)">
                                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.14 Kiek lankytojų aptarnauta interneto skaitykloje per ataskaitinį laikotarpį */}
                        <Card title="2.14 Kiek lankytojų aptarnauta interneto skaitykloje per ataskaitinį laikotarpį? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={['visitors_stats', 'institution_staff']} label="2.14.2 Institucijos darbuotojų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['visitors_stats', 'lecturers']} label="2.14.3 Lektorių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['visitors_stats', 'event_participants']} label="2.14.4 Institucijos renginių dalyvių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['visitors_stats', 'region_teachers']} label="2.14.5 Regiono švietimo darbuotojų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['visitors_stats', 'students']} label="2.14.6 Mokinių">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['visitors_stats', 'other', 'count']} label="2.14.7 Kitų">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item name={['visitors_stats', 'other', 'label']} label="Kitų kategorijos pavadinimas">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.15 Kiek renginių vyko apie informacinių technologijų naudojimą */}
                        <Card title="2.15 Kiek renginių vyko apie informacinių technologijų naudojimą? (išskirkite iš bendro seminarų, kursų ir paskaitų skaičiaus)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={['it_events_stats', 'seminar']} label="2.15.1 Seminarai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['it_events_stats', 'course']} label="2.15.2 Kursai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['it_events_stats', 'lecture']} label="2.15.3 Paskaitos">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Display it_events_stats_total */}
                            <Row style={{ marginTop: 16 }}>
                                <Col span={24}>
                                    <div className="counts-sum">
                                        <span className="counts-sum-label">
                                            2.15.4 Iš viso:
                                        </span>
                                        <span className="counts-sum-value">
                                            {internetFacility?.it_events_stats_total}
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.16 Ar institucija organizuoja mokymus nuotoliniu būdu */}
                        <Card title="2.16 Ar institucija organizuoja mokymus nuotoliniu būdu?" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="has_remote_training" label="Organizuoja mokymus nuotoliniu būdu" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 2.17 Kiek renginių vyko nuotoliniu būdu */}
                        <Card title="2.17 Kiek renginių vyko nuotoliniu būdu? (išskirkite iš bendro seminarų, kursų ir paskaitų skaičiaus)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name={['remote_events_stats', 'seminar']} label="2.17.1 Seminarai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['remote_events_stats', 'course']} label="2.17.2 Kursai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name={['remote_events_stats', 'lecture']} label="2.17.3 Paskaitos">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row style={{ marginTop: 16 }}>
                                <Col span={24}>
                                    <div className="counts-sum">
                                        <span className="counts-sum-label">
                                            2.17.4 Iš viso:
                                        </span>
                                        <span className="counts-sum-value">
                                            {internetFacility?.remote_events_stats_total}
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScInternet;
